import React, { useState, useEffect, useRef } from 'react';
import AccordionItem from './AccordionItem';
import CustomNumpad from './CustomNumpad';
import { CLASSES, RARITIES, EMBLEMS, TRIBE_ICONS } from '../constants';
import { FaSearch, FaFont, FaBookmark, FaSnowflake, FaScroll, FaExchangeAlt, FaExclamationTriangle, FaTrash } from "react-icons/fa";
import { getMistakes } from '../utils/cardChecker';

const getStatsConfig = (type) => {
  const cardType = type?.toLowerCase();

  switch (cardType) {
    case 'minion':
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
        { label: 'Attack', key: 'Attack', colorClass: 'text-warning' },
        { label: 'Health', key: 'Health', colorClass: 'text-danger' }
      ];
    case 'weapon':
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
        { label: 'Attack', key: 'Attack', colorClass: 'text-warning' },
        { label: 'Health', key: 'Health', colorClass: 'text-danger' }
      ];
    case 'location':
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
        { label: 'Health', key: 'Health', colorClass: 'text-danger' }
      ];
    case 'hero':
    case 'herocard':
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
        { label: 'Health', key: 'Health', colorClass: 'text-danger' }
      ];
    case 'spell':
    case 'heropower':
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
      ];
    default:
      return [
        { label: 'Cost', key: 'Cost', colorClass: 'text-primary' },
        { label: 'Attack', key: 'Attack', colorClass: 'text-warning' },
        { label: 'Health', key: 'Health', colorClass: 'text-danger' }
      ];
  }
};



export default function EditorSection({ cardParams, updateParam, updateNestedParam, overwriteAllParams, onOpenModal, onReset, actualFontSize, onCreate }) {
  const [showDualClass, setShowDualClass] = useState(false);
  const [activeNumpadField, setActiveNumpadField] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [isTribeFocused, setIsTribeFocused] = useState(false);

  const statsRef = useRef(null);
  const textareaRef = useRef(null);

  const handleTextFormatting = (openTag, closeTag) => {
    const textarea = textareaRef.current;
    
    if (!textarea) {
      updateParam('Text', `${cardParams.Text}${openTag}${closeTag}`);
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = cardParams.Text || '';

    const textBefore = currentText.substring(0, start);
    const selectedText = currentText.substring(start, end);
    const textAfter = currentText.substring(end);

    const newText = `${textBefore}${openTag}${selectedText}${closeTag}${textAfter}`;
    updateParam('Text', newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + openTag.length, end + openTag.length);
    }, 0);
  };

  const getStatValue = (statKey) => {
  if (statKey === 'Cost') {
    return cardParams.Cost?.Value !== undefined ? cardParams.Cost.Value : cardParams.Cost;
  }
  
  // If the specific key (e.g., Durability, Armor) exists on cardParams, use it
  if (cardParams[statKey] !== undefined) {
    return cardParams[statKey];
  }
  
  // Fallback: If Durability or Armor are stored in the Health field, retrieve Health
  if ((statKey === 'Durability' || statKey === 'Armor') && cardParams.Health !== undefined) {
    return cardParams.Health;
  }
  
  return '';
};

  const handleStatClick = (stat) => {
    setActiveNumpadField(stat);
    
    if (stat === 'Cost') {
      updateNestedParam('Cost', 'Value', '');
    } else {
      updateParam(stat, '');
    }
  };

  const handleResetClick = () => {
    if (cardParams.Text && cardParams.Text.trim() !== '') {
      setShowResetConfirm(true);
    } else {
      onReset();
    }
  };

  const confirmReset = () => {
    onReset();
    setShowResetConfirm(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !e.target.closest('.custom-numpad') &&
        statsRef.current &&
        !statsRef.current.contains(e.target)
      ) {
        setActiveNumpadField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeNumpadField && statsRef.current) {
      setTimeout(() => {
        const numpadEl = document.querySelector('.custom-numpad.stuck');
        const numpadHeight = numpadEl ? numpadEl.offsetHeight : 350; 
        const statsRect = statsRef.current.getBoundingClientRect();
        const numpadTop = window.innerHeight - numpadHeight;
        const overlap = statsRect.bottom - numpadTop + 20;

        if (overlap > 0) {
          window.scrollBy({ top: overlap, behavior: 'smooth' });
        } else if (statsRect.top < 60) {
          window.scrollBy({ top: statsRect.top - 80, behavior: 'smooth' });
        }
      }, 50); 
    }
  }, [activeNumpadField, cardParams.Cost.Type]);

  const isStandardEmblem = EMBLEMS.some((group) =>
    group.options.some((opt) => opt.id === cardParams.Emblem)
  );

  const emblemImagePath = isStandardEmblem
    ? `/images/selectorEmblems/${cardParams.Emblem}.png`
    : `/assets/emblems/${cardParams.Emblem}.png`;

  const mistakes = getMistakes(cardParams);

  const handleFixAll = (e) => {
    e.preventDefault();
    let currentParams = cardParams;
    let loopActive = true;
    let safetyCounter = 0;

    while (loopActive && safetyCounter < 50) {
      safetyCounter++;
      
      const currentMistakes = getMistakes(currentParams);
      const fixableMistakes = currentMistakes.filter(m => m.apply !== null && m.apply !== undefined);

      if (fixableMistakes.length === 0) {
        loopActive = false;
        break;
      }

      let stateChanged = false;
      for (const mistake of fixableMistakes) {
        const newParams = mistake.apply(currentParams);
        
        if (newParams.Text !== currentParams.Text || newParams.Title !== currentParams.Title) {
          currentParams = newParams;
          stateChanged = true;
          break;
        }
      }

      if (!stateChanged) {
        loopActive = false;
      }
    }

    overwriteAllParams(currentParams);
  };

  // Helper to parse entered text and render matched standard icons inline
  const renderFormattedTribeText = () => {
    if (!cardParams.Tribe || cardParams.Tribe.trim() === '') {
      return <span className="text-muted">e.g. Dragon, Mech</span>;
    }

    const segments = cardParams.Tribe.split(',');
    return segments.map((seg, idx) => {
      const trimmed = seg.trim();
      
      // Perform a case-insensitive lookup inside our standard icon list
      const matchedKey = Object.keys(TRIBE_ICONS).find(
        key => key.toLowerCase() === trimmed.toLowerCase()
      );
      const iconPath = matchedKey ? TRIBE_ICONS[matchedKey] : null;

      return (
        <span key={idx} className="d-inline-flex align-items-center me-1">
          {iconPath && (
            <img 
              src={iconPath} 
              alt={trimmed} 
              style={{ width: '24px', height: '24px', objectFit: 'contain' }} 
              className="me-1"
            />
          )}
          <span>{trimmed}</span>
          {idx < segments.length - 1 && <span className="text-muted">,</span>}
        </span>
      );
    });
  };

  return (
    <form className="pb-0" onSubmit={(e) => e.preventDefault()}>
      <div className="accordion shadow-sm" id="editorAccordion">
        
        <AccordionItem id="core" title="CORE DETAILS" icon={<FaScroll className="text-warning" />} defaultOpen>
          <div className="row g-3">
            <div className="col-12 mt-4">
              <label className="form-label d-flex justify-content-between align-items-center w-100 mb-2">
                <span>Card Class</span> 
                <span className={`submenu-pill class-toggle-badge ${showDualClass ? 'secondary-active' : ''}`} onClick={() => setShowDualClass(!showDualClass)}>
                  <FaExchangeAlt /> {showDualClass ? 'Back' : 'Dual'}
                </span>
              </label>
              
              <div className="grid-selector">
                {showDualClass && (
                  <label key="None">
                    <input 
                      type="radio" 
                      name="dualclass" 
                      checked={cardParams.DualClass === '' || cardParams.DualClass === 'None'}
                      onChange={() => updateParam('DualClass', '')} 
                    />
                    <img src="/images/classes-main/none-64.png" alt="None" />
                  </label>
                )}

                {CLASSES
                  .filter(cls => !showDualClass || cls !== 'Neutral') 
                  .map(cls => (
                    <label key={cls}>
                      <input 
                        type="radio" 
                        name={showDualClass ? "dualclass" : "cardclass"} 
                        checked={showDualClass ? cardParams.DualClass === cls : cardParams.Class === cls}
                        onChange={() => updateParam(showDualClass ? 'DualClass' : 'Class', cls)}
                      />
                      <img src={`/images/classes-main/${cls.toLowerCase()}-128.webp`} alt={cls} />
                    </label>
                  ))
                }

                <label 
                  className={`custom-add-btn flex-column gap-1 ${
                    (showDualClass 
                      ? (cardParams.DualClass !== '' && cardParams.DualClass !== 'None' && !CLASSES.includes(cardParams.DualClass))
                      : (!CLASSES.includes(cardParams.Class))
                    ) ? 'active-custom' : ''
                  }`} 
                  onClick={() => onOpenModal(showDualClass ? 'customDualClass' : 'customClass')}
                >
                  <FaSearch />
                  <span style={{ fontSize: '0.55rem', fontWeight: 'bold' }}>Custom</span>
                </label>
              </div>
            </div>

            <div className="col-12 mt-4">
              <label className="form-label d-flex justify-content-between align-items-center w-100 mb-2">
                <span>Rarity Gem</span> 
                <span>

                  {/* Dragon Overwrite Badge */}
                  <label 
                    className={`submenu-pill toggle-badge ${
                      cardParams.Overwrite.Dragon === 'show' || 
                      (cardParams.Rarity === 'Legendary' && cardParams.Overwrite.Dragon !== 'hide') 
                        ? 'active' 
                        : ''
                    }`} 
                    onClick={() => {
                      const isCurrentlyActive = cardParams.Overwrite.Dragon === 'show' || (cardParams.Rarity === 'Legendary' && cardParams.Overwrite.Dragon !== 'hide');
                      if (cardParams.Rarity === 'Legendary') {
                        updateNestedParam('Overwrite', 'Dragon', isCurrentlyActive ? 'hide' : '');
                      } else {
                        updateNestedParam('Overwrite', 'Dragon', isCurrentlyActive ? '' : 'show');
                      }
                    }}
                  >
                    <span className="status-light"></span> Dragon
                  </label>

{/* Emblem Overwrite Badge */}
                  <label 
                    className={`submenu-pill toggle-badge ms-2 ${
                      cardParams.Overwrite.Emblem === 'show' || 
                      (cardParams.Rarity !== 'Free' && cardParams.Overwrite.Emblem !== 'hide') 
                        ? 'active' 
                        : ''
                    }`} 
                    onClick={() => {
                      const isCurrentlyActive = cardParams.Overwrite.Emblem === 'show' || (cardParams.Rarity !== 'Free' && cardParams.Overwrite.Emblem !== 'hide');
                      if (cardParams.Rarity === 'Free') {
                        updateNestedParam('Overwrite', 'Emblem', isCurrentlyActive ? '' : 'show');
                      } else {
                        updateNestedParam('Overwrite', 'Emblem', isCurrentlyActive ? 'hide' : '');
                      }
                    }}
                  >
                    <span className="status-light"></span> Emblem
                  </label>
                </span>
              </label>
              <div className="grid-selector grid-selector-gems" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                {RARITIES.map(rarity => (
                  <label key={rarity}>
                    <input 
                      type="radio" 
                      checked={cardParams.Rarity === rarity}
                      onChange={() => updateParam('Rarity', rarity)}
                    />
                    <img src={`/images/gem/${rarity.toLowerCase()}-128.webp`} alt={rarity} />
                  </label>
                ))}
              </div>
            </div>

{/* Stats row with Custom Numpad Integration */}
{/* Stats row with Custom Numpad Integration */}
<div ref={statsRef} className="row g-3 text-center mb-4 mt-2 position-relative justify-content-center">
  {getStatsConfig(cardParams.Type).map((stat) => {
    // Determine column width based on how many elements are shown
    const statsCount = getStatsConfig(cardParams.Type).length;
    const colClass = statsCount === 3 ? 'col-4' : statsCount === 2 ? 'col-5' : 'col-6';

    return (
      <div className={colClass} key={stat.label}>
        <label className={`form-label ${stat.colorClass}`}>
          {stat.label}
        </label>
        <input 
          type="text" 
          readOnly
          className="form-control text-center fw-bold fs-4 pointer wood-indent" 
          placeholder="0"
          value={getStatValue(stat.key)}
          onClick={() => handleStatClick(stat.label)} 
        />
      </div>
    );
  })}

              
              {/* Floating Custom Numpad (Duplicate div wrapper removed!) */}
              {activeNumpadField && (
                <CustomNumpad 
                  activeField={activeNumpadField}
                  currentValue={activeNumpadField === 'Cost' ? cardParams.Cost.Value : cardParams[activeNumpadField]}
                  costType={ cardParams.Cost.Type }
                  onChange={(val) => activeNumpadField === 'Cost' ? updateNestedParam('Cost', 'Value', val) : updateParam(activeNumpadField, val)}
                  onCostTypeChange={(type) => updateNestedParam('Cost', 'Type', type)}
                  onClose={() => setActiveNumpadField(null)}
                />
              )}

              {cardParams.Cost.Type === 'BGStars' && (
                <div className="col-12 mt-3">
                  <label className="form-label d-block text-center text-muted mb-2">Chronum Cost</label>
                  <div className="d-flex justify-content-center gap-3 align-items-center">
                    {[
                      { value: '', img: 'ChronumCost.png' },
                      { value: 1, img: 'ChronumCost.png' },
                      { value: 2, img: 'ChronumCost.png' },
                      { value: 3, img: 'ChronumCost.png' }
                    ].map((item) => {
                      const isActive = (cardParams.ChronumCost || '') === item.value;
                      return (
                        <button
                          key={item.img}
                          type="button"
                          className={`btn p-1 rounded border ${isActive ? 'btn-primary border-primary' : 'btn-outline-dark border-secondary'}`}
                          style={{ width: '50px', height: '50px', background: isActive ? 'rgba(13, 110, 253, 0.1)' : 'transparent' }}
                          onClick={() => updateParam('ChronumCost', item.value)}
                        >
                          <img 
                            src={`/assets/${item.img}`} 
                            alt={item.value || 'None'} 
                            className="img-fluid h-100 object-fit-contain" 
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="col-md-8 mt-1">
              <label className="form-label d-block text-start">Expansion Emblem</label>
              <div className="d-flex gap-2 align-items-center">
                <div className="emblem-preview-wrapper">
                  <div 
                    className="carved-icon" 
                    style={{ '--icon': `url('${emblemImagePath}')` }}
                  ></div>
                </div>
                
                <select 
                  className="form-select flex-grow-1 wood-indent" 
                  value={cardParams.Emblem} 
                  onChange={(e) => updateParam('Emblem', e.target.value)}
                >
                  {!isStandardEmblem && cardParams.Emblem && (
                    <option value={cardParams.Emblem}>
                      Custom: {cardParams.Emblem}
                    </option>
                  )}

                  {EMBLEMS.map((group) => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                
                <button 
                  type="button" 
                  className="btn btn-outline-secondary btn-hc-search px-3" 
                  onClick={() => onOpenModal('customEmblem')}
                ></button>
              </div>
            </div>
          </div>
        </AccordionItem>

        <AccordionItem id="text" title="CARD TEXTS & TYPE" icon={<FaFont />} defaultOpen>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label d-block text-start">Card Name</label>
              <input type="text" className="form-control form-control-lg wood-indent" placeholder="e.g. Leeroy Jenkins" value={cardParams.Title} onChange={(e) => updateParam('Title', e.target.value)} />
            </div>

            <div className="col-12">
              <label className="form-label d-block text-start">Type / Subtype</label>
              <div className="input-group">
                {/* <input type="text" className="form-control wood-indent" placeholder="e.g. Dragon, Mech" value={cardParams.Tribe} onChange={(e) => updateParam('Tribe', e.target.value)} /> */}

                {isTribeFocused ? (
                  <input 
                    type="text" 
                    className="form-control wood-indent" 
                    placeholder="e.g. Dragon, Mech" 
                    value={cardParams.Tribe} 
                    onChange={(e) => updateParam('Tribe', e.target.value)} 
                    onBlur={() => setIsTribeFocused(false)}
                    autoFocus
                  />
                ) : (
                  <div 
                    className="form-control wood-indent d-flex align-items-center flex-wrap gap-1 pointer text-start"
                    style={{ minHeight: '38px', cursor: 'text' }}
                    onClick={() => setIsTribeFocused(true)}
                  >
                    {renderFormattedTribeText()}
                  </div>
                )}

                <button className="btn btn-outline-secondary btn-hc-tribes" type="button" onClick={() => onOpenModal('tribe')}></button>
              </div>
            </div>

            <div className="col-12">
              <div className="d-flex justify-content-between align-items-end mb-1">
                <label className="form-label mb-0">Card Description</label>
                <div className="btn-group">
                  <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => handleTextFormatting('[b]', '[/b]')}><b>B</b></button>
                  <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => handleTextFormatting('[i]', '[/i]')}><i>I</i></button>
                </div>
              </div>
              <textarea 
                ref={textareaRef}
                className="form-control wood-indent" 
                rows="4" 
                placeholder="Use [b]bold[/b] or [i]italic[/i]..." 
                value={cardParams.Text} 
                onChange={(e) => updateParam('Text', e.target.value)}>
              </textarea>
            </div>

            <div className="col-12 d-flex align-items-center justify-content-between gap-3">
              <label className="form-label mb-0">Font Size Adjust:</label>
              <div className="btn-group">
                <button type="button" className="btn btn-outline-secondary" onClick={() => updateNestedParam('Overwrite', 'FontSize', cardParams.Overwrite.FontSize - 1)}>-</button>
                <button type="button" className="btn btn-secondary disabled" style={{minWidth: '100px'}}>
                  {actualFontSize ? `${actualFontSize}` : 'Auto'} 
                  <small className="ms-1" style={{opacity: 0.6}}>({cardParams.Overwrite.FontSize > 0 ? '+' : ''}{cardParams.Overwrite.FontSize})</small>
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => updateNestedParam('Overwrite', 'FontSize', cardParams.Overwrite.FontSize + 1)}>+</button>
              </div>
            </div>
          </div>
        </AccordionItem>

        <AccordionItem id="banners" title="BANNERS & RIBBONS" icon={<FaBookmark className="text-success" />}>
          <div className="banners-scroller no-swipe mb-3">
            {['banner_empty2.webp', 'banner_prepare.webp', 'banner_forge.webp', 'banner_tradeable.webp', 'banner_triclass_0-1.webp', 'banner_triclass_0-2.webp', 'banner_triclass_0-3.webp', 'banner_sc_zerg.webp', 'banner_sc_protoss.webp', 'banner_sc_terran.webp'].map(banner => {
              const isMapEmpty = banner === 'banner_empty2.webp';
              const bannerValue = isMapEmpty ? '' : banner; 
              const isActive = cardParams.Banner === bannerValue;
              
              return (
                <div 
                  key={banner} 
                  className={`banner-slot ${isActive ? 'active-slot' : ''} ${isMapEmpty ? 'empty-only' : ''}`}
                  onClick={() => updateParam('Banner', bannerValue)} 
                >
                  <img src="/images/mobile/banner_empty2.webp" className="banner-empty-bg" alt="empty indent"/>
                  {!isMapEmpty && (
                    <img src={`/assets/${banner}`} className="banner-top-img" alt="banner overlay"/>
                  )}
                </div>
              );
            })}
            
            <div className="scroller-divider"></div>
            
            <img 
              src="/assets/right-banner-sc-us2.webp" 
              className={`standalone-banner ${cardParams.RightBanner ? 'active-banner' : ''}`} 
              onClick={() => updateParam('RightBanner', cardParams.RightBanner ? '' : 'right-banner-sc-us2.webp')} 
              alt="Right Banner"
            />
          </div>
          <button type="button" className="btn btn-outline-info w-100" onClick={() => onOpenModal('customBanner')}>
            <FaSearch /> Browse Community Banners
          </button>
        </AccordionItem>

        <AccordionItem id="runes" title="DEATH KNIGHT RUNES" icon={<FaSnowflake className="text-info" />}>
          <div className="row g-3 text-center">
            {['Slot1', 'Slot2', 'Slot3'].map((slot, idx) => (
              <div className="col-4" key={slot}>
                <label className="form-label text-white">Slot {idx + 1}</label>
                <div className="rune-selector">
                  {['None', 'Blood', 'Frost', 'Unholy'].map(rune => {
                    const runeVal = rune === 'None' ? '' : `CardRunes_DeathKnight_${rune}.webp`;
                    return (
                      <div 
                        key={rune} 
                        className={`rune-option ${cardParams.Runes[slot] === runeVal ? 'active' : ''}`}
                        onClick={() => updateNestedParam('Runes', slot, runeVal)}
                      >
                        {rune === 'None' ? <span></span> : <img src={`/assets/${runeVal}`} style={{width: 20}} alt={rune} />} {rune}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </AccordionItem>

{/* CONDITIONAL CARD CHECKER TAB */}
        {mistakes.length > 0 && (
          <AccordionItem id="checker" title="CARD CHECKER" icon={<FaExclamationTriangle className="text-warning" />}>
             <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-warning fw-bold">{mistakes.length} warning{mistakes.length > 1 ? 's' : ''} found!</span>
                {mistakes.some(m => m.fixable !== false) && (
                  <button className="btn btn-sm btn-warning fw-bold" onClick={handleFixAll}>Auto-Fix All</button>
                )}
             </div>
             
             <ul className="list-group list-group-flush border border-secondary rounded shadow-sm">
               {mistakes.map(m => (
                  <li className="list-group-item bg-dark text-light border-secondary d-flex flex-column flex-sm-row justify-content-between align-items-sm-center py-3 gap-2" key={m.id}>
                     <div>
                       <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{m.message}</div>
                       {m.fixLabel && <div className="text-info mt-1" style={{ fontSize: '0.8rem' }}><i className="fa fa-wrench me-1"></i> {m.fixLabel}</div>}
                     </div>
                     {m.apply && (
                       <button 
                         className="btn btn-sm btn-outline-success flex-shrink-0" 
                         onClick={(e) => { e.preventDefault(); overwriteAllParams(m.apply(cardParams)); }}
                       >
                         Apply Fix
                       </button>
                     )}
                  </li>
               ))}
             </ul>
          </AccordionItem>
        )}

      </div>

      <div className="d-flex gap-2 mt-4 px-3">
        <button 
          type="button" 
          className="btn btn-danger button-create button-reset py-3 shadow" 
          style={{ width: '80px' }} 
          onClick={handleResetClick}
        >
          <FaTrash />
        </button>
        <button type="button" className="btn btn-export button-create flex-grow-1 py-3 rounded-3 shadow" onClick={onCreate}> Create</button>
      </div>

      {/* Confirmation Modal */}
      {showResetConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-dark text-light border-secondary">
              <div className="modal-header border-secondary">
                <h5 className="modal-title text-warning d-flex align-items-center gap-2">
                  <FaExclamationTriangle /> Reset Card?
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowResetConfirm(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">Are you sure you want to clear your changes and reset the editor?</p>
              </div>
              <div className="modal-footer border-secondary">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowResetConfirm(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmReset}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ height: activeNumpadField ? '400px' : '0px' }}></div>
    </form>
  );
}